import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { AppStateService } from "../app-state.service";

type ResettableFlag = boolean | "";
type TextOrBooleanState = string | boolean;
type ReaderBaseUrlState = string[] | Record<string, unknown>[];

/**
 * DeviceConfigStateService
 * --------------------------------------------------------------------
 * Configure-device modal / read-device / touch-dim / box-quantity /
 * reader status / logout coordination streams.
 */
@Injectable({ providedIn: "root" })
export class DeviceConfigStateService {
  // --- Configure-device modal --------------------------------------
  readonly triggerConfigureDevice = new BehaviorSubject<boolean>(false);
  readonly getTriggerConfigureDevice: Observable<boolean> =
    this.triggerConfigureDevice.asObservable();
  readonly closeConfigureDeviceModal = new BehaviorSubject<boolean>(false);

  // --- Read-device / form-input ------------------------------------
  readonly readDeviceSubject = new BehaviorSubject<TextOrBooleanState>("");
  readonly getReadDeviceSubject: Observable<TextOrBooleanState> = this.readDeviceSubject.asObservable();
  readonly formInputValidation = new BehaviorSubject<ResettableFlag>("");
  readonly getformInputValidation: Observable<ResettableFlag> =
    this.formInputValidation.asObservable();
  readonly simpleSetConnectReadDevice = new BehaviorSubject<boolean>(false);

  // --- Reader ------------------------------------------------------
  readonly readerBaseUrl = new BehaviorSubject<ReaderBaseUrlState>([]);
  readonly getReaderBaseUrl: Observable<ReaderBaseUrlState> = this.readerBaseUrl.asObservable();
  readonly readerLoad = new BehaviorSubject<string>("");

  // --- Touch-dim scratch pad --------------------------------------
  touchDimObj: Record<string, unknown> = {};

  // --- Show-next / cancel action toggle (read-device flow) ---------
  showNextAndCancel = false;

  // --- Refresh ZTV data --------------------------------------------
  readonly refreshZtvData = new BehaviorSubject<string>("");

  // --- Logout ------------------------------------------------------
  readonly logoutSubject = new BehaviorSubject<string>("");

  // --- Confirm button + box quantity + box size --------------------
  private readonly cnfBtnClickedSubject = new BehaviorSubject<boolean>(false);
  readonly cnfBtnClicked$: Observable<boolean> = this.cnfBtnClickedSubject.asObservable();

  private readonly boxQuantityValue = new BehaviorSubject<number>(0);
  readonly getBoxQuantityValue: Observable<number> = this.boxQuantityValue.asObservable();

  private readonly userInputBoxValue = new BehaviorSubject<number | null>(null);
  readonly getUserInputBoxValue: Observable<number | null> =
    this.userInputBoxValue.asObservable();

  // --- Device 12nc -------------------------------------------------
  private readonly device12ncSubject = new BehaviorSubject<string>("");

  constructor(private readonly appState: AppStateService) {
    const stored = this.appState.getRefreshZtvData();
    if (typeof stored === "string") {
      const dataZtv = JSON.stringify(JSON.parse(stored));
      this.refreshZtvData.next(dataZtv);
    }
  }

  sendReadDevice(value: TextOrBooleanState): void {
    this.readDeviceSubject.next(value);
  }
  setReadDeviceStatusViaSimpleSet(data: boolean): void {
    this.simpleSetConnectReadDevice.next(data);
  }
  setReaderBaseUrl(data: ReaderBaseUrlState): void {
    this.readerBaseUrl.next(data);
  }
  relaodForReader(data: string): void {
    this.readerLoad.next(data);
  }
  setFormInputValidation(data: ResettableFlag): void {
    this.formInputValidation.next(data);
  }

  logout(data: string): void {
    this.logoutSubject.next(data);
  }

  getCnfBtnClicked(): boolean {
    return this.cnfBtnClickedSubject.getValue();
  }
  setCnfBtnClicked(value: boolean): void {
    this.cnfBtnClickedSubject.next(value);
  }

  setBoxQuantityValue(value: number): void {
    this.boxQuantityValue.next(value);
  }
  setUserInputBoxValue(value: number | null): void {
    this.userInputBoxValue.next(value);
  }

  setDevice12nc(value: string): void {
    this.device12ncSubject.next(value);
  }
  getDevice12nc(): Observable<string> {
    return this.device12ncSubject.asObservable();
  }
}
